import React, { useState, useEffect } from 'react';
import { theme, commonStyles } from '../styles';
import { personalInfo } from '../data';

// ============================================
// 📧 CONFIGURATION EMAILJS
// 1. Crée un compte sur https://www.emailjs.com/
// 2. Crée un "Email Service" (connecte ton email Orange ou Gmail)
// 3. Crée un "Email Template" avec les variables: {{from_name}}, {{from_email}}, {{subject}}, {{message}}
// 4. Remplace les valeurs ci-dessous par les tiennes
// ============================================
const EMAILJS_CONFIG = {
  serviceId: 'service_l9mw2bu',      // Ex: 'service_abc123'
  templateId: 'template_sqlg92c',    // Ex: 'template_xyz789'
  publicKey: 'S_HFSsif_C-Z0kxNy',      // Ex: 'AbCdEfGhIjKlMnOp'
};

const ContactPage = ({ isMobile }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, answer: '' });
  const [captchaInput, setCaptchaInput] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    generateCaptcha();
    // Charger le script EmailJS
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js';
    script.async = true;
    document.body.appendChild(script);
    
    script.onload = () => {
      window.emailjs.init(EMAILJS_CONFIG.publicKey);
    };
    
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptcha({ num1, num2, answer: (num1 + num2).toString() });
    setCaptchaInput('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Le sujet est requis';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Le message doit contenir au moins 10 caractères';
    }
    
    if (captchaInput !== captcha.answer) {
      newErrors.captcha = 'Réponse incorrecte au captcha';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Envoi via EmailJS
      await window.emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        {
          from_name: formData.name,
          from_email: formData.email,
          subject: formData.subject,
          message: formData.message,
          to_email: personalInfo.email,
        }
      );
      
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      generateCaptcha();
    } catch (error) {
      console.error('Erreur envoi email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  return (
    <section style={{
      ...commonStyles.page,
      ...(isMobile ? commonStyles.pageMobile : {}),
    }}>
      <h1 style={{
        ...commonStyles.pageTitle,
        ...(isMobile ? commonStyles.pageTitleMobile : {}),
      }}>
        Contact
      </h1>
      <p style={{
        ...commonStyles.pageSubtitle,
        ...(isMobile ? commonStyles.pageSubtitleMobile : {}),
      }}>
        N'hésitez pas à me contacter pour discuter de vos projets
      </p>

      <div style={{
        ...styles.contactGrid,
        gap: isMobile ? '1.5rem' : '3rem',
      }}>
        {/* Contact Info Card */}
        <ContactInfoCard isMobile={isMobile} />

        {/* Contact Form */}
        <div style={styles.contactForm}>
          {submitStatus === 'success' && (
            <div style={{
              ...commonStyles.successMessage,
              fontSize: isMobile ? '0.85rem' : '1rem',
            }}>
              ✅ Message envoyé avec succès ! Je vous répondrai bientôt.
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div style={{
              ...styles.errorMessage,
              fontSize: isMobile ? '0.85rem' : '1rem',
            }}>
              ❌ Erreur lors de l'envoi. Veuillez réessayer ou me contacter directement par email.
            </div>
          )}

          <FormField
            label="Nom"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            error={errors.name}
            placeholder="Votre nom"
            isMobile={isMobile}
          />

          <FormField
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
            placeholder="votre@email.com"
            isMobile={isMobile}
          />

          <FormField
            label="Sujet"
            name="subject"
            type="text"
            value={formData.subject}
            onChange={handleChange}
            error={errors.subject}
            placeholder="Sujet de votre message"
            isMobile={isMobile}
          />

          <FormField
            label="Message"
            name="message"
            type="textarea"
            value={formData.message}
            onChange={handleChange}
            error={errors.message}
            placeholder="Votre message..."
            isMobile={isMobile}
          />

          {/* Captcha */}
          <div style={{
            ...styles.captchaBox,
            padding: isMobile ? '1rem' : '1.5rem',
          }}>
            <div style={styles.captchaHeader}>
              <span style={{ fontSize: isMobile ? '1rem' : '1.25rem' }}>🔒</span>
              <span style={{
                ...styles.captchaTitle,
                fontSize: isMobile ? '0.85rem' : '0.95rem',
              }}>
                Vérification Anti-Robot
              </span>
            </div>
            <div style={styles.captchaQuestion}>
              <span style={{
                ...styles.captchaMath,
                fontSize: isMobile ? '1.25rem' : '1.5rem',
              }}>
                {captcha.num1} + {captcha.num2} = ?
              </span>
              <button 
                type="button" 
                onClick={generateCaptcha}
                style={styles.captchaRefresh}
                title="Nouveau calcul"
              >
                🔄
              </button>
            </div>
            <input
              type="text"
              value={captchaInput}
              onChange={(e) => {
                setCaptchaInput(e.target.value);
                if (errors.captcha) {
                  setErrors(prev => ({ ...prev, captcha: '' }));
                }
              }}
              style={{
                ...commonStyles.input,
                ...(isMobile ? commonStyles.inputMobile : {}),
                ...styles.captchaInput,
                ...(errors.captcha ? commonStyles.inputError : {}),
                maxWidth: isMobile ? '120px' : '150px',
              }}
              placeholder="?"
            />
            {errors.captcha && (
              <span style={commonStyles.errorText}>{errors.captcha}</span>
            )}
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{
              ...styles.submitButton,
              ...(isSubmitting ? styles.submitButtonDisabled : {}),
              padding: isMobile ? '1rem 1.5rem' : '1.25rem 2rem',
              fontSize: isMobile ? '1rem' : '1.1rem',
            }}
          >
            {isSubmitting ? '📤 Envoi en cours...' : '📧 Envoyer le Message'}
          </button>
        </div>
      </div>
    </section>
  );
};

// Contact Info Card Component
const ContactInfoCard = ({ isMobile }) => {
  const contactItems = [
    { icon: '📧', label: 'Email', value: personalInfo.email, href: `mailto:${personalInfo.email}` },
    { icon: '📱', label: 'Téléphone', value: personalInfo.phone, href: `tel:${personalInfo.phone.replace(/\s/g, '')}` },
    { icon: '📍', label: 'Localisation', value: `${personalInfo.postalCode} ${personalInfo.location.split('(')[0].trim()}` },
    { icon: '🔗', label: 'GitHub', value: 'github.com/clement14A', href: personalInfo.social.github },
    { icon: '💼', label: 'LinkedIn', value: 'clement auger', href: personalInfo.social.linkedin },
  ];

  return (
    <div style={{
      ...styles.contactInfo,
      padding: isMobile ? '1.25rem' : '2rem',
    }}>
      <h2 style={{
        ...styles.contactInfoTitle,
        fontSize: isMobile ? '1.1rem' : '1.25rem',
      }}>
        Informations
      </h2>
      
      {contactItems.map((item, index) => (
        <div 
          key={index}
          style={{
            ...styles.contactItem,
            marginBottom: isMobile ? '1rem' : '1.5rem',
          }}
        >
          <span style={{
            ...styles.contactIcon,
            fontSize: isMobile ? '1.25rem' : '1.5rem',
          }}>
            {item.icon}
          </span>
          <div>
            <span style={styles.contactLabel}>{item.label}</span>
            {item.href ? (
              <a 
                href={item.href}
                target={item.href.startsWith('http') ? '_blank' : undefined}
                rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                style={{
                  ...styles.contactValue,
                  fontSize: isMobile ? '0.9rem' : '1rem',
                }}
              >
                {item.value}
              </a>
            ) : (
              <span style={{
                ...styles.contactValue,
                fontSize: isMobile ? '0.9rem' : '1rem',
              }}>
                {item.value}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Form Field Component
const FormField = ({ label, name, type, value, onChange, error, placeholder, isMobile }) => (
  <div style={commonStyles.formGroup}>
    <label style={{
      ...commonStyles.label,
      ...(isMobile ? commonStyles.labelMobile : {}),
    }}>
      {label} *
    </label>
    {type === 'textarea' ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        style={{
          ...commonStyles.textarea,
          ...(error ? commonStyles.inputError : {}),
          padding: isMobile ? '0.875rem' : '1rem',
          fontSize: isMobile ? '0.9rem' : '1rem',
          minHeight: isMobile ? '100px' : '120px',
        }}
        placeholder={placeholder}
        rows={5}
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        style={{
          ...commonStyles.input,
          ...(isMobile ? commonStyles.inputMobile : {}),
          ...(error ? commonStyles.inputError : {}),
        }}
        placeholder={placeholder}
      />
    )}
    {error && <span style={commonStyles.errorText}>{error}</span>}
  </div>
);

const styles = {
  contactGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
  },
  contactInfo: {
    background: theme.colors.backgroundCard,
    borderRadius: '16px',
    border: '1px solid rgba(0, 217, 255, 0.1)',
    height: 'fit-content',
  },
  contactInfoTitle: {
    fontWeight: '600',
    color: theme.colors.primary,
    marginBottom: '1.5rem',
  },
  contactItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1rem',
  },
  contactIcon: {
    marginTop: '0.25rem',
    flexShrink: 0,
  },
  contactLabel: {
    display: 'block',
    color: theme.colors.textDark,
    fontSize: '0.85rem',
    marginBottom: '0.25rem',
  },
  contactValue: {
    color: theme.colors.text,
    textDecoration: 'none',
    transition: theme.transition.normal,
    display: 'block',
    wordBreak: 'break-all',
  },
  contactForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  errorMessage: {
    background: 'rgba(255, 107, 107, 0.1)',
    border: '1px solid rgba(255, 107, 107, 0.3)',
    color: theme.colors.error,
    padding: '1rem',
    borderRadius: '10px',
    fontWeight: '500',
  },
  captchaBox: {
    background: theme.colors.backgroundCard,
    borderRadius: '12px',
    border: '2px solid rgba(0, 217, 255, 0.2)',
  },
  captchaHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  captchaTitle: {
    color: theme.colors.primary,
    fontWeight: '600',
  },
  captchaQuestion: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  captchaMath: {
    fontWeight: '700',
    color: theme.colors.text,
    fontFamily: theme.fonts.mono,
  },
  captchaRefresh: {
    background: 'transparent',
    border: 'none',
    fontSize: '1.25rem',
    cursor: 'pointer',
    padding: '0.5rem',
    borderRadius: '8px',
    transition: theme.transition.normal,
    minHeight: '44px',
    minWidth: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  captchaInput: {
    textAlign: 'center',
    fontFamily: theme.fonts.mono,
    fontSize: '1.25rem',
  },
  submitButton: {
    background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`,
    color: theme.colors.background,
    borderRadius: '12px',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    transition: theme.transition.normal,
    boxShadow: '0 4px 20px rgba(0, 217, 255, 0.3)',
    fontFamily: theme.fonts.main,
    minHeight: '56px',
  },
  submitButtonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
};

export default ContactPage;